import { useEffect, useMemo, useState } from "react";
import { fetchAxios } from "../API/fetchAxios";
import {
  BRANDING_UPDATED_EVENT,
  notifyBrandingUpdated,
} from "../Pages/Public/partnerAppTheme";
import {
  loadBrandingSession,
  persistBrandingForHost,
  saveBrandingSession,
} from "./brandingStorage";
import { loadItem, removeItem, saveItem } from "../Utils";
import { getAuthToken } from "../Services/authToken";
import logoDefault from "../Assets/valut-logo-dark.svg";
import logoWhite from "../Assets/valut-logo-gold.png";

/** Hardcoded Valut branding — login must paint without waiting on theme API. */
export const DEFAULT_VALUT_FAVICON =
  "https://cdn.valut.services/logo/App%20Store%20-%201x.png?v=2";

export const DEFAULT_VALUT_SITE_OPTIONS = {
  branch_name: "Valut - Conta digital",
  title: "Valut - Private Global Banking.",
  favicon: DEFAULT_VALUT_FAVICON,
  login_image: logoWhite,
  logo_default: logoDefault,
  logo_white: logoWhite,
  site: "https://valut.app",
  app: { android_url: "", ios_url: "" },
  branch_id: "1",
  instance_id: null,
  resolved_host: null,
  resolved_at: null,
  is_matrix: true,
  colors: {
    background: {
      original: "#b79568",
      dark: "#003967",
      darkest: "#001F38",
      light: "#0096FF",
      lightest: "#50B7FF",
    },
    feedback: {
      error: "",
      error_bg: "",
      warning: "",
      warning_bg: "",
      info: "",
      info_bg: "",
      success: "",
      success_bg: "",
    },
    fonts: {
      title: "",
      subtitle: "",
      paragraph: "",
      text: "",
      disable_text: "",
    },
    secondary: {
      secondary_01: "",
      secondary_02: "",
      secondary_03: "",
      secondary_04: "",
      secondary_05: "",
    },
    primary: {
      original: "#b79568",
      dark: "#003967",
      darkest: "#001F38",
      light: "#0096FF",
      lightest: "#50B7FF",
    },
  },
};

const hasThemeColors = (value) => Boolean(value?.colors?.primary?.original);

/** Flat branding object (cached Options or nested `branding` blob). */
const isBrandingPayload = (value) =>
  Boolean(
    value &&
      (value.branch_id ||
        value.branch_name ||
        value.logo_default ||
        value.title ||
        value.instance_id != null ||
        hasThemeColors(value))
  );

/** True when API returns DNS host resolution wrapper. */
const isPublicBrandingResponse = (value) =>
  Boolean(
    value &&
      (value.branding ||
        value.instance_id != null ||
        value.instanceId != null ||
        value.app_config ||
        value.appConfig)
  );

/** True when a cached/API logo URL is a legacy Valut bundled PNG (not white-label). */
const isLegacyValutPngLogo = (url) =>
  typeof url === "string" &&
  /valut-logo[^/]*\.png/i.test(url);

/** Ignore blank logo fields from API/DB so we keep bundled defaults or the sibling variant. */
const normalizeLogoUrl = (url) => {
  const trimmed = String(url || "").trim();
  return trimmed || undefined;
};

/** Bust browser cache for local partner assets after regenerating PNGs. */
const withBrandingAssetCacheBust = (url) => {
  if (!url || !url.startsWith("/branding/")) {
    return url;
  }
  if (/[?&]v=/.test(url)) {
    return url;
  }
  return `${url}${url.includes("?") ? "&" : "?"}v=20260831`;
};

const resolveLogoUrl = (incoming, key, bundledDefault) => {
  const raw = incoming?.[key];
  if (isLegacyValutPngLogo(raw)) {
    return bundledDefault;
  }
  return withBrandingAssetCacheBust(normalizeLogoUrl(raw) || bundledDefault);
};

/**
 * Merge partial / cached site options onto Valut defaults (immediate paint safe).
 * Exported for unit tests.
 */
export const mergeSiteOptions = (incoming) => ({
  ...DEFAULT_VALUT_SITE_OPTIONS,
  ...incoming,
  // Never let API/cache wipe branding back to an empty/React favicon.
  favicon: incoming?.favicon || DEFAULT_VALUT_SITE_OPTIONS.favicon,
  title: incoming?.title || DEFAULT_VALUT_SITE_OPTIONS.title,
  // Keep Valut wordmarks on the SVG assets — never rehydrate old PNG bundles.
  logo_default: resolveLogoUrl(
    incoming,
    "logo_default",
    DEFAULT_VALUT_SITE_OPTIONS.logo_default,
  ),
  logo_white: resolveLogoUrl(
    incoming,
    "logo_white",
    DEFAULT_VALUT_SITE_OPTIONS.logo_white,
  ),
  login_image: resolveLogoUrl(
    incoming,
    "login_image",
    DEFAULT_VALUT_SITE_OPTIONS.login_image,
  ),
  colors: hasThemeColors(incoming)
    ? incoming.colors
    : DEFAULT_VALUT_SITE_OPTIONS.colors,
  app: {
    ...DEFAULT_VALUT_SITE_OPTIONS.app,
    ...(incoming?.app && typeof incoming.app === "object" ? incoming.app : {}),
  },
  login:
    incoming?.login && typeof incoming.login === "object"
      ? { ...incoming.login }
      : undefined,
  branch_id:
    incoming?.branch_id != null && incoming.branch_id !== ""
      ? String(incoming.branch_id)
      : incoming?.instance_id != null
        ? String(incoming.instance_id)
        : DEFAULT_VALUT_SITE_OPTIONS.branch_id,
  instance_id:
    incoming?.instance_id != null ? Number(incoming.instance_id) || incoming.instance_id : null,
  resolved_host: incoming?.resolved_host ?? null,
  resolved_at: incoming?.resolved_at ?? null,
  is_matrix:
    typeof incoming?.is_matrix === "boolean"
      ? incoming.is_matrix
      : DEFAULT_VALUT_SITE_OPTIONS.is_matrix,
});

/**
 * Map GET /v1/public/branding (or /v1/mobile/branding) body → Options shape.
 * Also accepts legacy flat branding payloads.
 */
export const applyBrandingPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  if (isPublicBrandingResponse(payload)) {
    const branding =
      payload.branding && typeof payload.branding === "object"
        ? payload.branding
        : {};
    const appConfig =
      (payload.app_config && typeof payload.app_config === "object"
        ? payload.app_config
        : null) ||
      (payload.appConfig && typeof payload.appConfig === "object"
        ? payload.appConfig
        : {}) ||
      {};
    const appFromConfig =
      (appConfig.app && typeof appConfig.app === "object" ? appConfig.app : null) ||
      (branding.app && typeof branding.app === "object" ? branding.app : {}) ||
      {};

    const instanceId = payload.instance_id ?? payload.instanceId ?? null;
    const site =
      appConfig.site_url ||
      appConfig.siteUrl ||
      branding.site ||
      DEFAULT_VALUT_SITE_OPTIONS.site;

    return mergeSiteOptions({
      ...branding,
      site,
      login:
        branding.login && typeof branding.login === "object"
          ? branding.login
          : undefined,
      app: {
        android_url:
          appFromConfig.android_url ||
          appFromConfig.androidUrl ||
          branding?.app?.android_url ||
          "",
        ios_url:
          appFromConfig.ios_url ||
          appFromConfig.iosUrl ||
          branding?.app?.ios_url ||
          "",
      },
      support_url: appConfig.support_url || appConfig.supportUrl || "",
      terms_url: appConfig.terms_url || appConfig.termsUrl || "",
      privacy_url: appConfig.privacy_url || appConfig.privacyUrl || "",
      show_valut_footer:
        appConfig.login?.show_valut_footer ??
        appConfig.login?.showValutFooter ??
        true,
      instance_id: instanceId,
      public_host: payload.public_host ?? payload.publicHost ?? null,
      resolved_host: payload.resolved_host ?? payload.resolvedHost ?? null,
      resolved_at: new Date().toISOString(),
      is_matrix: Boolean(payload.is_matrix ?? payload.isMatrix),
      branch_id:
        instanceId != null
          ? String(instanceId)
          : branding.branch_id || DEFAULT_VALUT_SITE_OPTIONS.branch_id,
    });
  }

  if (isBrandingPayload(payload)) {
    return mergeSiteOptions(payload);
  }

  return null;
};

const MATRIX_HOSTS = new Set([
  "localhost",
  "127.0.0.1",
  "app.valut.app",
  "carteira.valut.app",
  "valut.app",
  "ib.valut.app",
  "ib.valut.us",
]);

const needsAuthBrandingBootstrap = () =>
  /^\/auth(\/|$)/.test(String(window.location.pathname || ""));

/** Host used for DNS white-label resolution (browser hostname only). */
export const resolveEffectiveBrandingHost = () => {
  const hostname = String(window.location.hostname || "").trim().toLowerCase();
  if (hostname && hostname !== "localhost" && hostname !== "127.0.0.1") {
    return hostname;
  }
  return "";
};

export const expectsPartnerBranding = () => {
  const host = resolveEffectiveBrandingHost();
  return Boolean(host && !MATRIX_HOSTS.has(host));
};

export const isPartnerBrandOptions = (options) =>
  Boolean(options?.instance_id && options?.is_matrix === false);

/** True when it is safe to paint login/public UI for the current host. */
export const isBrandingReadyForHost = (options) => {
  if (!options) return false;

  const expectedHost = resolveEffectiveBrandingHost();

  if (expectsPartnerBranding()) {
    const resolvedHost = String(options.resolved_host || "").toLowerCase();
    // Alias that Baas resolves to the Valut matrix (is_matrix true) can paint.
    if (options.is_matrix === true && resolvedHost === expectedHost) {
      return true;
    }
    return isPartnerBrandOptions(options) && resolvedHost === expectedHost;
  }

  if (needsAuthBrandingBootstrap()) {
    return Boolean(options.resolved_at);
  }

  return true;
};

const resolveInitialOptions = () => {
  const expectedHost = resolveEffectiveBrandingHost();
  const isPartner = expectsPartnerBranding();

  if (isPartner && expectedHost) {
    const sessionBrand = loadBrandingSession(expectedHost);
    if (
      sessionBrand &&
      isPartnerBrandOptions(sessionBrand) &&
      String(sessionBrand.resolved_host || "").toLowerCase() === expectedHost
    ) {
      saveItem(sessionBrand, "Options");
      return sessionBrand;
    }
  }

  const cached = loadItem("Options");

  if (cached && (isBrandingPayload(cached) || isPublicBrandingResponse(cached))) {
    const merged = applyBrandingPayload(cached) || mergeSiteOptions(cached);

    if (isPartner && expectedHost) {
      if (
        isPartnerBrandOptions(merged) &&
        String(merged.resolved_host || "").toLowerCase() === expectedHost
      ) {
        saveItem(merged, "Options");
        saveBrandingSession(expectedHost, merged);
        return merged;
      }
      if (!isPartnerBrandOptions(merged)) {
        removeItem("Options");
      }
      return null;
    }

    if (!isPartner && isPartnerBrandOptions(merged)) {
      removeItem("Options");
      return DEFAULT_VALUT_SITE_OPTIONS;
    }

    saveItem(merged, "Options");
    return merged;
  }

  if (isPartner && expectedHost) {
    return null;
  }

  return DEFAULT_VALUT_SITE_OPTIONS;
};

/**
 * Fetch branding by Host (public) or authenticated mobile endpoint.
 * Persists Options when successful. Safe to call after login.
 */
export const fetchInstanceBranding = async ({ authenticated = false } = {}) => {
  try {
    const response = await fetchAxios({
      verb: "GET",
      context: authenticated ? "themeMobile" : "theme",
    });
    const next = applyBrandingPayload(response?.data);
    if (!next) {
      return null;
    }
    saveItem(next, "Options");
    persistBrandingForHost(next, resolveEffectiveBrandingHost());
    notifyBrandingUpdated(next);
    return next;
  } catch {
    return null;
  }
};

export const useGetSiteOptions = () => {
  const initial = useMemo(() => resolveInitialOptions(), []);
  const [options, setOptions] = useState(() => initial);
  const [brandingReady, setBrandingReady] = useState(() =>
    isBrandingReadyForHost(initial),
  );
  const [brandingError, setBrandingError] = useState(null);
  const [bootstrapKey, setBootstrapKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      const authenticated = Boolean(getAuthToken());
      if (!cancelled) {
        setBrandingError(null);
      }

      if (isBrandingReadyForHost(initial)) {
        const refreshed = await fetchInstanceBranding({ authenticated });
        if (!cancelled && refreshed) {
          setOptions(refreshed);
        }
        if (!cancelled) {
          setBrandingReady(true);
          setBrandingError(null);
        }
        return;
      }

      const next = await fetchInstanceBranding({ authenticated });
      if (!cancelled) {
        const expectedHost = resolveEffectiveBrandingHost();
        const sessionFallback =
          expectsPartnerBranding() && expectedHost
            ? loadBrandingSession(expectedHost)
            : null;

        if (expectsPartnerBranding()) {
          const resolved = next || sessionFallback || initial;
          const ready = isBrandingReadyForHost(resolved);
          setOptions(resolved);
          setBrandingReady(ready);
          setBrandingError(
            ready
              ? null
              : "Não foi possível carregar o tema deste parceiro. Confirme MobileService/Baas e a coluna CarteiraWhiteLabel no banco, depois tente de novo.",
          );
        } else {
          setOptions(next || DEFAULT_VALUT_SITE_OPTIONS);
          setBrandingReady(true);
          setBrandingError(null);
        }
      }
    };

    bootstrap();

    const onBrandingUpdated = (event) => {
      const next = event?.detail;
      if (!next) return;
      setOptions(next);
      const ready = isBrandingReadyForHost(next);
      setBrandingReady(ready);
      if (ready) {
        setBrandingError(null);
      }
    };

    window.addEventListener(BRANDING_UPDATED_EVENT, onBrandingUpdated);

    return () => {
      cancelled = true;
      window.removeEventListener(BRANDING_UPDATED_EVENT, onBrandingUpdated);
    };
  }, [initial, bootstrapKey]);

  return {
    data: options,
    brandingReady,
    brandingError,
    retryBranding: () => setBootstrapKey((key) => key + 1),
    setOptions,
  };
};
